/**
 * Second-Chance Consent Hook
 *
 * Manages logic for showing a contextual consent prompt after high-value user milestones.
 * Only shown if analytics was rejected or never set, with a 30-day cooldown after dismissal.
 */

import { useState, useCallback, useMemo } from 'react'
import { useCookieConsent } from '../contexts/CookieConsentContext'
import { isNativeMobile } from '../mobile/capacitor'

const STORAGE_KEY = 'optioneer_second_chance_consent'
const COOLDOWN_DAYS = 30
const COOLDOWN_MS = COOLDOWN_DAYS * 24 * 60 * 60 * 1000

interface SecondChanceState {
  dismissedAt: number | null
}

function getStoredState(): SecondChanceState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch {
    // Ignore localStorage errors
  }
  return { dismissedAt: null }
}

function setStoredState(state: SecondChanceState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Ignore localStorage errors
  }
}

export function useSecondChanceConsent() {
  const { consent, savePreferences } = useCookieConsent()
  const [visible, setVisible] = useState(false)
  const nativeMobile = isNativeMobile()

  // Check if we can show the prompt
  const canShowPrompt = useMemo(() => {
    if (nativeMobile) return false

    // Only if analytics is not already enabled
    const analyticsEnabled = consent?.categories.analytics === true
    if (analyticsEnabled) return false

    // Check cooldown
    const state = getStoredState()
    if (state.dismissedAt !== null) {
      const elapsed = Date.now() - state.dismissedAt
      if (elapsed < COOLDOWN_MS) {
        return false
      }
    }

    return true
  }, [consent?.categories.analytics, nativeMobile])

  // Trigger the prompt (call this after a milestone event)
  const triggerPrompt = useCallback(() => {
    if (canShowPrompt) {
      setVisible(true)
    }
  }, [canShowPrompt])

  // User dismisses the prompt
  const dismissPrompt = useCallback(() => {
    setVisible(false)
    setStoredState({ dismissedAt: Date.now() })
  }, [])

  // User enables analytics
  const enableAnalytics = useCallback(() => {
    setVisible(false)
    savePreferences({
      functional: consent?.categories.functional ?? false,
      analytics: true,
      marketing: consent?.categories.marketing ?? false,
    })
  }, [consent?.categories.functional, consent?.categories.marketing, savePreferences])

  return {
    /** Whether the prompt should be visible */
    showPrompt: visible,
    /** Whether we're eligible to show a prompt (passes all checks) */
    canShowPrompt,
    /** Trigger the prompt to show (call after milestone) */
    triggerPrompt,
    /** Dismiss the prompt (sets 30-day cooldown) */
    dismissPrompt,
    /** Enable analytics only (closes prompt) */
    enableAnalytics,
  }
}
